import { computed, ref, watch, type Ref } from "vue";
import {
  Decimal,
  MAX_ACCOMPANYING_ADULTS_PER_TRANSACTION,
  ZERO_CHF,
  addDecimals,
  decimalFromString,
  formatDecimal,
  multiplyDecimal,
  type DecimalString,
  type PriceCategory,
  type PublicEvent,
  type PurchaseItem,
} from "@evka/shared";

export interface CartLine {
  category: PriceCategory;
  quantity: number;
  unitPrice: DecimalString;
  subtotal: DecimalString;
}

export interface UseCart {
  isMember: Ref<boolean>;
  adultQty: Ref<number>;
  childQty: Ref<number>;
  adultMax: Ref<number>;
  childMax: Ref<number>;
  adultCategory: Ref<PriceCategory>;
  childCategory: Ref<PriceCategory>;
  adultUnitPrice: Ref<DecimalString>;
  childUnitPrice: Ref<DecimalString>;
  lines: Ref<CartLine[]>;
  totalQuantity: Ref<number>;
  totalAmount: Ref<DecimalString>;
  totalDecimal: Ref<Decimal>;
  items: Ref<PurchaseItem[]>;
  hasItems: Ref<boolean>;
  canSubmit: Ref<boolean>;
  submitBlockedReason: Ref<string | null>;
  setAdultQty: (n: number) => void;
  setChildQty: (n: number) => void;
}

const PAYING_MAX_PER_CATEGORY = 50; // matches purchaseItemSchema.quantity max

function clampQty(n: number, max: number): number {
  if (!Number.isFinite(n) || Number.isNaN(n)) return 0;
  const i = Math.trunc(n);
  if (i < 0) return 0;
  if (i > max) return max;
  return i;
}

/**
 * Cart logic for the purchase form.
 *
 * Two physical "rows" — Erwachsene / Kinder — each with a quantity. A single
 * "Mitglied"-toggle decides which of the four price categories each row maps to
 * (member vs. non-member). Totals are computed exclusively through `Decimal`
 * helpers from `@evka/shared/money` (SPEC §2 and CLAUDE.md non-negotiable #11).
 *
 * When Mitglied is on, the adult row is the "Begleitperson" slot — free, and
 * capped per SPEC §4.1 at MAX_ACCOMPANYING_ADULTS_PER_TRANSACTION. A cart that
 * only contains accompanying adults has total "0.00" and is not submittable
 * (the server would reject it as well — see createPurchaseSchema).
 */
export function useCart(event: Ref<PublicEvent | null>): UseCart {
  const isMember = ref(false);
  const adultQty = ref(0);
  const childQty = ref(0);

  const adultCategory = computed<PriceCategory>(() =>
    isMember.value ? "adult_member" : "adult_nonmember",
  );
  const childCategory = computed<PriceCategory>(() =>
    isMember.value ? "child_member" : "child_nonmember",
  );

  const adultMax = computed<number>(() =>
    isMember.value
      ? MAX_ACCOMPANYING_ADULTS_PER_TRANSACTION
      : PAYING_MAX_PER_CATEGORY,
  );
  const childMax = computed<number>(() => PAYING_MAX_PER_CATEGORY);

  const adultUnitPrice = computed<DecimalString>(
    () => event.value?.prices[adultCategory.value] ?? ZERO_CHF,
  );
  const childUnitPrice = computed<DecimalString>(
    () => event.value?.prices[childCategory.value] ?? ZERO_CHF,
  );

  const lines = computed<CartLine[]>(() => {
    const out: CartLine[] = [];
    if (adultQty.value > 0) {
      out.push({
        category: adultCategory.value,
        quantity: adultQty.value,
        unitPrice: adultUnitPrice.value,
        subtotal: formatDecimal(multiplyDecimal(adultUnitPrice.value, adultQty.value)),
      });
    }
    if (childQty.value > 0) {
      out.push({
        category: childCategory.value,
        quantity: childQty.value,
        unitPrice: childUnitPrice.value,
        subtotal: formatDecimal(multiplyDecimal(childUnitPrice.value, childQty.value)),
      });
    }
    return out;
  });

  const totalDecimal = computed<Decimal>(() => {
    if (lines.value.length === 0) return decimalFromString(ZERO_CHF);
    return addDecimals(...lines.value.map((l) => l.subtotal));
  });

  const totalAmount = computed<DecimalString>(() => formatDecimal(totalDecimal.value));
  const totalQuantity = computed(() => adultQty.value + childQty.value);
  const hasItems = computed(() => totalQuantity.value > 0);

  const items = computed<PurchaseItem[]>(() =>
    lines.value.map((l) => ({ category: l.category, quantity: l.quantity })),
  );

  const canSubmit = computed(
    () => hasItems.value && totalDecimal.value.gt(0),
  );

  const submitBlockedReason = computed<string | null>(() => {
    if (!hasItems.value) return "buy.submit.disabledNoTickets";
    if (!totalDecimal.value.gt(0)) return "buy.errors.freeOnlyNeedsPayingTicket";
    return null;
  });

  function setAdultQty(n: number) {
    adultQty.value = clampQty(n, adultMax.value);
  }
  function setChildQty(n: number) {
    childQty.value = clampQty(n, childMax.value);
  }

  // Flipping Mitglied on mid-flow can leave adultQty above the lower cap;
  // re-clamp synchronously so the stepper never visibly holds an out-of-range
  // value for a tick.
  watch(
    isMember,
    () => {
      if (adultQty.value > adultMax.value) adultQty.value = adultMax.value;
    },
    { flush: "sync" },
  );

  return {
    isMember,
    adultQty,
    childQty,
    adultMax,
    childMax,
    adultCategory,
    childCategory,
    adultUnitPrice,
    childUnitPrice,
    lines,
    totalQuantity,
    totalAmount,
    totalDecimal,
    items,
    hasItems,
    canSubmit,
    submitBlockedReason,
    setAdultQty,
    setChildQty,
  };
}
